<script setup>
import ProductManagementItemTable from '@frontJs/components/tables/projectManagement/sea/ProductManagementItemTable.vue';
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import DisplayVariablesAction from '@js/components/actions/DisplayVariablesAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { h, onMounted, reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, googleAdsProductManagement, variableInformation } = defineProps(
    {
        tenant: {
            required: true,
            type: Object,
        },
        googleAdsProductManagement: {
            required: false,
            type: Object,
        },
        variableInformation: {
            required: true,
            type: Object,
        },
    },
);

const { route } = useRoute();

const selectedTab = ref();

const loading = ref(true);

const eCommerceStores = ref([]);

const form = reactive({
    cmsStore: googleAdsProductManagement
        ? googleAdsProductManagement.cmsStore
        : null,
    generateProductFeed: googleAdsProductManagement
        ? googleAdsProductManagement.generateProductFeed
        : false,
    generateCpcFeed: googleAdsProductManagement
        ? googleAdsProductManagement.generateCpcFeed
        : false,
    updateCpcAutomatically: googleAdsProductManagement
        ? googleAdsProductManagement.updateCpcAutomatically
        : false,
    variables: {
        calculatedProductCost: googleAdsProductManagement
            ? googleAdsProductManagement.variables.calculatedProductCost
            : '',
        maximumExpenses: googleAdsProductManagement
            ? googleAdsProductManagement.variables.maximumExpenses
            : '',
        roasTarget: googleAdsProductManagement
            ? googleAdsProductManagement.variables.roasTarget
            : '',
        startDate: googleAdsProductManagement?.variables?.startDate ?? '',
        zombie: {
            pmaxCampaign:
                googleAdsProductManagement?.variables?.zombie?.pmaxCampaign ??
                '',
            impressionsThreshold:
                googleAdsProductManagement?.variables?.zombie
                    ?.impressionsThreshold ?? '',
            clicksThreshold:
                googleAdsProductManagement?.variables?.zombie
                    ?.clicksThreshold ?? '',
            operator:
                googleAdsProductManagement?.variables?.zombie?.operator ?? 'or',
        },
        excludedNamePatterns:
            googleAdsProductManagement?.variables?.excludedNamePatterns ?? '',
        formulas: {
            r0DiscoveryNoSalesSite: googleAdsProductManagement
                ? googleAdsProductManagement.variables.formulas
                      .r0DiscoveryNoSalesSite
                : '',
            r0DiscoveryWithSalesSite: googleAdsProductManagement
                ? googleAdsProductManagement.variables.formulas
                      .r0DiscoveryWithSalesSite
                : '',
            r1ProfitableWinner: googleAdsProductManagement
                ? googleAdsProductManagement.variables.formulas
                      .r1ProfitableWinner
                : '',
            r1ProfitableDormant: googleAdsProductManagement
                ? googleAdsProductManagement.variables.formulas
                      .r1ProfitableDormant
                : '',
            r2NotProfitableProfitableSiteWinner: googleAdsProductManagement
                ? googleAdsProductManagement.variables.formulas
                      .r2NotProfitableProfitableSiteWinner
                : '',
            r2NotProfitableProfitableSiteDormant: googleAdsProductManagement
                ? googleAdsProductManagement.variables.formulas
                      .r2NotProfitableProfitableSiteDormant
                : '',
            r3NotProfitableNotProfitableSite: googleAdsProductManagement
                ? googleAdsProductManagement.variables.formulas
                      .r3NotProfitableNotProfitableSite
                : '',
        },
        feedMappings: {
            id: googleAdsProductManagement
                ? googleAdsProductManagement.variables.feedMappings.id
                : '',
            customLabel0: googleAdsProductManagement
                ? googleAdsProductManagement.variables.feedMappings.customLabel0
                : '',
            customLabel1: googleAdsProductManagement
                ? googleAdsProductManagement.variables.feedMappings.customLabel1
                : '',
            customLabel2: googleAdsProductManagement
                ? googleAdsProductManagement.variables.feedMappings.customLabel2
                : '',
            customLabel3: googleAdsProductManagement
                ? googleAdsProductManagement.variables.feedMappings.customLabel3
                : '',
            customLabel4: googleAdsProductManagement
                ? googleAdsProductManagement.variables.feedMappings.customLabel4
                : '',
        },
        googleAds: {
            campaigns: {
                r0DiscoveryNoSalesSite:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.campaigns &&
                    googleAdsProductManagement.variables.googleAds.campaigns
                        .r0DiscoveryNoSalesSite
                        ? googleAdsProductManagement.variables.googleAds
                              .campaigns.r0DiscoveryNoSalesSite
                        : null,
                r0DiscoveryWithSalesSite:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.campaigns &&
                    googleAdsProductManagement.variables.googleAds.campaigns
                        .r0DiscoveryWithSalesSite
                        ? googleAdsProductManagement.variables.googleAds
                              .campaigns.r0DiscoveryWithSalesSite
                        : null,
                r1ProfitableWinner:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.campaigns &&
                    googleAdsProductManagement.variables.googleAds.campaigns
                        .r1ProfitableWinner
                        ? googleAdsProductManagement.variables.googleAds
                              .campaigns.r1ProfitableWinner
                        : null,
                r1ProfitableDormant:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.campaigns &&
                    googleAdsProductManagement.variables.googleAds.campaigns
                        .r1ProfitableDormant
                        ? googleAdsProductManagement.variables.googleAds
                              .campaigns.r1ProfitableDormant
                        : null,
                r2NotProfitableProfitableSite:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.campaigns &&
                    googleAdsProductManagement.variables.googleAds.campaigns
                        .r2NotProfitableProfitableSite
                        ? googleAdsProductManagement.variables.googleAds
                              .campaigns.r2NotProfitableProfitableSite
                        : null,
                r3NotProfitableNotProfitableSite:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.campaigns &&
                    googleAdsProductManagement.variables.googleAds.campaigns
                        .r3NotProfitableNotProfitableSite
                        ? googleAdsProductManagement.variables.googleAds
                              .campaigns.r3NotProfitableNotProfitableSite
                        : null,
            },
            adGroups: {
                r0DiscoveryNoSalesSite:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.adGroups &&
                    googleAdsProductManagement.variables.googleAds.adGroups
                        .r0DiscoveryNoSalesSite
                        ? googleAdsProductManagement.variables.googleAds
                              .adGroups.r0DiscoveryNoSalesSite
                        : null,
                r0DiscoveryWithSalesSite:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.adGroups &&
                    googleAdsProductManagement.variables.googleAds.adGroups
                        .r0DiscoveryWithSalesSite
                        ? googleAdsProductManagement.variables.googleAds
                              .adGroups.r0DiscoveryWithSalesSite
                        : null,
                r1ProfitableWinner:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.adGroups &&
                    googleAdsProductManagement.variables.googleAds.adGroups
                        .r1ProfitableWinner
                        ? googleAdsProductManagement.variables.googleAds
                              .adGroups.r1ProfitableWinner
                        : null,
                r1ProfitableDormant:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.adGroups &&
                    googleAdsProductManagement.variables.googleAds.adGroups
                        .r1ProfitableDormant
                        ? googleAdsProductManagement.variables.googleAds
                              .adGroups.r1ProfitableDormant
                        : null,
                r2NotProfitableProfitableSite:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.adGroups &&
                    googleAdsProductManagement.variables.googleAds.adGroups
                        .r2NotProfitableProfitableSite
                        ? googleAdsProductManagement.variables.googleAds
                              .adGroups.r2NotProfitableProfitableSite
                        : null,
                r3NotProfitableNotProfitableSite:
                    googleAdsProductManagement &&
                    googleAdsProductManagement.variables.googleAds &&
                    googleAdsProductManagement.variables.googleAds.adGroups &&
                    googleAdsProductManagement.variables.googleAds.adGroups
                        .r3NotProfitableNotProfitableSite
                        ? googleAdsProductManagement.variables.googleAds
                              .adGroups.r3NotProfitableNotProfitableSite
                        : null,
            },
        },
    },
});

const zombieOperators = [
    { value: 'or', label: 'OU (impressions OU clics sous le seuil)' },
    { value: 'and', label: 'ET (impressions ET clics sous le seuil)' },
];

const isSubmitting = ref(false);

const validationSchema = object({
    cmsStore: object().required('Ce champ est obligatoire.'),
    generateProductFeed: boolean().required('Ce champ est obligatoire.'),
    generateCpcFeed: boolean().required('Ce champ est obligatoire.'),
    updateCpcAutomatically: boolean().required('Ce champ est obligatoire.'),
    variables: object()
        .shape({
            calculatedProductCost: string().required(
                'Ce champ est obligatoire.',
            ),
            maximumExpenses: string().required('Ce champ est obligatoire.'),
            roasTarget: string().required('Ce champ est obligatoire.'),
            startDate: string().nullable(),
            zombie: object().shape({
                pmaxCampaign: string().nullable(),
                impressionsThreshold: string().nullable(),
                clicksThreshold: string().nullable(),
                operator: string().nullable(),
            }),
            excludedNamePatterns: string().nullable(),
            formulas: object().shape({
                r0DiscoveryNoSalesSite: string().required(
                    'Ce champ est obligatoire.',
                ),
                r0DiscoveryWithSalesSite: string().required(
                    'Ce champ est obligatoire.',
                ),
                r1ProfitableWinner: string().required(
                    'Ce champ est obligatoire.',
                ),
                r1ProfitableDormant: string().required(
                    'Ce champ est obligatoire.',
                ),
                r2NotProfitableProfitableSiteWinner: string().required(
                    'Ce champ est obligatoire.',
                ),
                r2NotProfitableProfitableSiteDormant: string().required(
                    'Ce champ est obligatoire.',
                ),
                r3NotProfitableNotProfitableSite: string().required(
                    'Ce champ est obligatoire.',
                ),
            }),
            feedMappings: object().shape({
                id: string().required('Ce champ est obligatoire.'),
                customLabel0: string().nullable(),
                customLabel1: string().nullable(),
                customLabel2: string().nullable(),
                customLabel3: string().nullable(),
                customLabel4: string().nullable(),
            }),
            googleAds: object().shape({
                campaigns: object().shape({
                    r0DiscoveryNoSalesSite: string().nullable(),
                    r0DiscoveryWithSalesSite: string().nullable(),
                    r1ProfitableWinner: string().nullable(),
                    r1ProfitableDormant: string().nullable(),
                    r2NotProfitableProfitableSite: string().nullable(),
                    r3NotProfitableNotProfitableSite: string().nullable(),
                }),
                adGroups: object().shape({
                    r0DiscoveryNoSalesSite: string().nullable(),
                    r0DiscoveryWithSalesSite: string().nullable(),
                    r1ProfitableWinner: string().nullable(),
                    r1ProfitableDormant: string().nullable(),
                    r2NotProfitableProfitableSite: string().nullable(),
                    r3NotProfitableNotProfitableSite: string().nullable(),
                }),
            }),
        })
        .nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.sea.postProductManagement', {
                tenant: tenant.id,
                ...(googleAdsProductManagement
                    ? {
                          googleAdsProductManagement:
                              googleAdsProductManagement.id,
                      }
                    : {}),
            }),
            {
                cmsStoreId: form.cmsStore.cmsStoreId,
                generateProductFeed: form.generateProductFeed,
                generateCpcFeed: form.generateCpcFeed,
                updateCpcAutomatically: form.updateCpcAutomatically,
                variables: form.variables,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.sea.productManagements',
                    tenant ? { tenant: tenant.id } : {},
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.sea.productManagements', {
                    tenant: tenant.id,
                }),
            );
        });
}

function updateData() {
    loading.value = true;

    Promise.all([
        axios.get(route('front.eCommerce.getStores', { tenant: tenant.id })),
    ])
        .then(function ([eCommerceStoresResponse]) {
            if (eCommerceStoresResponse.status === 200) {
                eCommerceStores.value = eCommerceStoresResponse.data;

                form.cmsStore = eCommerceStores.value.find(
                    (eCommerceStore) =>
                        eCommerceStore.cmsStoreId ===
                        (googleAdsProductManagement
                            ? googleAdsProductManagement.eCommerceStore
                                  .cmsStoreId
                            : null),
                );
            } else {
                toastError('Une erreur est survenue.');
            }
        })
        .finally(() => {
            loading.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.sea.productManagements', {
                    tenant: tenant.id,
                }),
            );
        });
}

onMounted(() => {
    updateData();
});
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <TabLayout
            v-model:selected-tab="selectedTab"
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                configuration: {
                    title: 'Configuration',
                    items: {
                        variables: {
                            title: 'Variables',
                            contentSlotName: 'variables',
                            forceMount: true,
                        },
                        formulas: {
                            title: 'Formules',
                            contentSlotName: 'formulas',
                            forceMount: true,
                        },
                        feedMappings: {
                            title: 'Mappings de flux',
                            contentSlotName: 'feedMappings',
                            forceMount: true,
                        },
                        googleAds: {
                            title: 'Google Ads',
                            contentSlotName: 'googleAds',
                            forceMount: true,
                        },
                    },
                    forceMount: true,
                },
                ...(googleAdsProductManagement
                    ? {
                          products: {
                              title: 'Produits',
                              content: h(ProductManagementItemTable, {
                                  tenant,
                                  googleAdsProductManagement,
                              }),
                              forceMount: false,
                          },
                      }
                    : {}),
            }"
            :loading="loading"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <ListboxField
                                v-model="form.cmsStore"
                                field-id="cmsStore"
                                field-name="cmsStore"
                                label="Magasin"
                                :options="eCommerceStores"
                                required
                            >
                                <template #buttonContent>
                                    <template v-if="form.cmsStore">
                                        {{ form.cmsStore.name }}
                                    </template>

                                    <template v-else> &nbsp; </template>
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <SwitchField
                                v-model="form.generateProductFeed"
                                label="Générer le flux produit"
                                field-name="generateProductFeed"
                                field-id="generateProductFeed"
                            />

                            <SwitchField
                                v-model="form.generateCpcFeed"
                                label="Générer le flux CPC"
                                field-name="generateCpcFeed"
                                field-id="generateCpcFeed"
                            />

                            <SwitchField
                                v-model="form.updateCpcAutomatically"
                                label="Mettre à jour le CPC automatiquement"
                                field-name="updateCpcAutomatically"
                                field-id="updateCpcAutomatically"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #variables>
                <FormColumn>
                    <FormContent>
                        <div class="mb-4 flex justify-end">
                            <DisplayVariablesAction
                                title="Variables de mapping"
                                :variables="variableInformation"
                            >
                                <template #trigger>
                                    <ActionButton type="secondary" size="small">
                                        Aide
                                    </ActionButton>
                                </template>
                            </DisplayVariablesAction>
                        </div>

                        <FormGrid :columns="1">
                            <TextareaField
                                v-model="form.variables.calculatedProductCost"
                                label="Coût produit calculé"
                                field-name="variables.calculatedProductCost"
                                field-id="variables.calculatedProductCost"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="form.variables.maximumExpenses"
                                label="Dépenses max"
                                field-name="variables.maximumExpenses"
                                field-id="variables.maximumExpenses"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="form.variables.roasTarget"
                                label="ROAS Cible"
                                field-name="variables.roasTarget"
                                field-id="variables.roasTarget"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <InputField
                                v-model="form.variables.startDate"
                                label="Date de début"
                                field-name="variables.startDate"
                                field-id="variables.startDate"
                                type="date"
                                legend="Aucune donnée antérieure à cette date n'est prise en compte dans les calculs (7, 30, 90, 365 jours, total et produits zombies). Laisser vide pour utiliser tout l'historique disponible."
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle>
                            Identification des produits zombies PMax
                        </FormTitle>

                        <FormGrid :columns="1">
                            <InputField
                                v-model="form.variables.zombie.pmaxCampaign"
                                label="Nom de la campagne PMax"
                                field-name="variables.zombie.pmaxCampaign"
                                field-id="variables.zombie.pmaxCampaign"
                                legend="Le calcul des produits zombies se base uniquement sur les impressions et clics de cette campagne PMax sur les 30 derniers jours. Laisser vide pour désactiver la détection."
                            />
                        </FormGrid>

                        <FormGrid :columns="2">
                            <InputField
                                v-model="
                                    form.variables.zombie.impressionsThreshold
                                "
                                type="number"
                                label="Seuil d'impressions (30 jours)"
                                field-name="variables.zombie.impressionsThreshold"
                                field-id="variables.zombie.impressionsThreshold"
                                legend="Un produit affiché moins de fois que ce seuil sur les 30 derniers jours est considéré comme zombie. Laisser vide (0) pour ne pas tenir compte des impressions."
                            />

                            <InputField
                                v-model="form.variables.zombie.clicksThreshold"
                                type="number"
                                label="Seuil de clics (30 jours)"
                                field-name="variables.zombie.clicksThreshold"
                                field-id="variables.zombie.clicksThreshold"
                                legend="Un produit ayant reçu moins de clics que ce seuil sur les 30 derniers jours est considéré comme zombie. Laisser vide (0) pour ne pas tenir compte des clics."
                            />

                            <ListboxField
                                v-model="form.variables.zombie.operator"
                                field-id="variables.zombie.operator"
                                field-name="variables.zombie.operator"
                                label="Combinaison des seuils"
                                :options="zombieOperators"
                                option-key="value"
                            >
                                <template #buttonContent>
                                    {{
                                        zombieOperators.find(
                                            (operator) =>
                                                operator.value ===
                                                form.variables.zombie.operator,
                                        )?.label
                                    }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle> Exclusion des produits par nom </FormTitle>

                        <TextareaField
                            v-model="form.variables.excludedNamePatterns"
                            label="Exclure les produits dont le nom contient (un motif par ligne)"
                            legend="Les produits correspondants restent dans le flux mais sont classés en campagne « R-Exclu » et ne reçoivent aucune mise à jour de CPC."
                            field-name="variables.excludedNamePatterns"
                            field-id="variables.excludedNamePatterns"
                            resize="vertical"
                            rows="4"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #formulas>
                <FormColumn>
                    <FormContent>
                        <div class="mb-4 flex justify-end">
                            <DisplayVariablesAction
                                title="Variables de mapping"
                                :variables="variableInformation"
                            >
                                <template #trigger>
                                    <ActionButton type="secondary" size="small">
                                        Aide
                                    </ActionButton>
                                </template>
                            </DisplayVariablesAction>
                        </div>

                        <FormGrid :columns="1">
                            <TextareaField
                                v-model="
                                    form.variables.formulas
                                        .r0DiscoveryNoSalesSite
                                "
                                label="R0-Découverte-SansVentesSite"
                                field-name="variables.formulas.r0DiscoveryNoSalesSite"
                                field-id="variables.formulas.r0DiscoveryNoSalesSite"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.formulas
                                        .r0DiscoveryWithSalesSite
                                "
                                label="R0-Découverte-AvecVentesSite"
                                field-name="variables.formulas.r0DiscoveryWithSalesSite"
                                field-id="variables.formulas.r0DiscoveryWithSalesSite"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.formulas.r1ProfitableWinner
                                "
                                label="R1-Rentable (Winner)"
                                field-name="variables.formulas.r1ProfitableWinner"
                                field-id="variables.formulas.r1ProfitableWinner"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.formulas.r1ProfitableDormant
                                "
                                label="R1-Rentable (Dormant)"
                                field-name="variables.formulas.r1ProfitableDormant"
                                field-id="variables.formulas.r1ProfitableDormant"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.formulas
                                        .r2NotProfitableProfitableSiteWinner
                                "
                                label="R2-PasRentable-RentableSite (Winner)"
                                field-name="variables.formulas.r2NotProfitableProfitableSiteWinner"
                                field-id="variables.formulas.r2NotProfitableProfitableSiteWinner"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.formulas
                                        .r2NotProfitableProfitableSiteDormant
                                "
                                label="R2-PasRentable-RentableSite (Dormant)"
                                field-name="variables.formulas.r2NotProfitableProfitableSiteDormant"
                                field-id="variables.formulas.r2NotProfitableProfitableSiteDormant"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.formulas
                                        .r3NotProfitableNotProfitableSite
                                "
                                label="R3-PasRentable-PasRentableSite"
                                field-name="variables.formulas.r3NotProfitableNotProfitableSite"
                                field-id="variables.formulas.r3NotProfitableNotProfitableSite"
                                required
                                resize="vertical"
                                rows="2"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #feedMappings>
                <FormColumn>
                    <FormContent>
                        <div class="mb-4 flex justify-end">
                            <DisplayVariablesAction
                                title="Variables de mapping"
                                :variables="variableInformation"
                            >
                                <template #trigger>
                                    <ActionButton type="secondary" size="small">
                                        Aide
                                    </ActionButton>
                                </template>
                            </DisplayVariablesAction>
                        </div>

                        <FormGrid :columns="1">
                            <TextareaField
                                v-model="form.variables.feedMappings.id"
                                label="ID"
                                field-name="variables.feedMappings.id"
                                field-id="variables.feedMappings.id"
                                required
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.feedMappings.customLabel0
                                "
                                label="Étiquette personnalisée 0"
                                field-name="variables.feedMappings.customLabel0"
                                field-id="variables.feedMappings.customLabel0"
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.feedMappings.customLabel1
                                "
                                label="Étiquette personnalisée 1"
                                field-name="variables.feedMappings.customLabel1"
                                field-id="variables.feedMappings.customLabel1"
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.feedMappings.customLabel2
                                "
                                label="Étiquette personnalisée 2"
                                field-name="variables.feedMappings.customLabel2"
                                field-id="variables.feedMappings.customLabel2"
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.feedMappings.customLabel3
                                "
                                label="Étiquette personnalisée 3"
                                field-name="variables.feedMappings.customLabel3"
                                field-id="variables.feedMappings.customLabel3"
                                resize="vertical"
                                rows="2"
                            />

                            <TextareaField
                                v-model="
                                    form.variables.feedMappings.customLabel4
                                "
                                label="Étiquette personnalisée 4"
                                field-name="variables.feedMappings.customLabel4"
                                field-id="variables.feedMappings.customLabel4"
                                resize="vertical"
                                rows="2"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #googleAds>
                <FormColumn>
                    <FormContent>
                        <FormTitle> Campagnes </FormTitle>

                        <FormGrid :columns="3">
                            <InputField
                                v-model="
                                    form.variables.googleAds.campaigns
                                        .r0DiscoveryNoSalesSite
                                "
                                label="Campagne - R0-Découverte-SansVentesSite"
                                field-name="variables.googleAds.campaigns.r0DiscoveryNoSalesSite"
                                field-id="variables.googleAds.campaigns.r0DiscoveryNoSalesSite"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.campaigns
                                        .r0DiscoveryWithSalesSite
                                "
                                label="Campagne - R0-Découverte-AvecVentesSite"
                                field-name="variables.googleAds.campaigns.r0DiscoveryWithSalesSite"
                                field-id="variables.googleAds.campaigns.r0DiscoveryWithSalesSite"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.campaigns
                                        .r1ProfitableWinner
                                "
                                label="Campagne - R1-Rentable (Winner)"
                                field-name="variables.googleAds.campaigns.r1ProfitableWinner"
                                field-id="variables.googleAds.campaigns.r1ProfitableWinner"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.campaigns
                                        .r1ProfitableDormant
                                "
                                label="Campagne - R1-Rentable (Dormant)"
                                field-name="variables.googleAds.campaigns.r1ProfitableDormant"
                                field-id="variables.googleAds.campaigns.r1ProfitableDormant"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.campaigns
                                        .r2NotProfitableProfitableSite
                                "
                                label="Campagne - R2-PasRentable-RentableSite"
                                field-name="variables.googleAds.campaigns.r2NotProfitableProfitableSite"
                                field-id="variables.googleAds.campaigns.r2NotProfitableProfitableSite"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.campaigns
                                        .r3NotProfitableNotProfitableSite
                                "
                                label="Campagne - R3-PasRentable-PasRentableSite"
                                field-name="variables.googleAds.campaigns.r3NotProfitableNotProfitableSite"
                                field-id="variables.googleAds.campaigns.r3NotProfitableNotProfitableSite"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle> Groupes d'annonces </FormTitle>

                        <FormGrid :columns="3">
                            <InputField
                                v-model="
                                    form.variables.googleAds.adGroups
                                        .r0DiscoveryNoSalesSite
                                "
                                label="Groupe d'annonce - R0-Découverte-SansVentesSite"
                                field-name="variables.googleAds.adGroups.r0DiscoveryNoSalesSite"
                                field-id="variables.googleAds.adGroups.r0DiscoveryNoSalesSite"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.adGroups
                                        .r0DiscoveryWithSalesSite
                                "
                                label="Groupe d'annonce - R0-Découverte-AvecVentesSite"
                                field-name="variables.googleAds.adGroups.r0DiscoveryWithSalesSite"
                                field-id="variables.googleAds.adGroups.r0DiscoveryWithSalesSite"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.adGroups
                                        .r1ProfitableWinner
                                "
                                label="Groupe d'annonce - R1-Rentable (Winner)"
                                field-name="variables.googleAds.adGroups.r1ProfitableWinner"
                                field-id="variables.googleAds.adGroups.r1ProfitableWinner"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.adGroups
                                        .r1ProfitableDormant
                                "
                                label="Groupe d'annonce - R1-Rentable (Dormant)"
                                field-name="variables.googleAds.adGroups.r1ProfitableDormant"
                                field-id="variables.googleAds.adGroups.r1ProfitableDormant"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.adGroups
                                        .r2NotProfitableProfitableSite
                                "
                                label="Groupe d'annonce - R2-PasRentable-RentableSite"
                                field-name="variables.googleAds.adGroups.r2NotProfitableProfitableSite"
                                field-id="variables.googleAds.adGroups.r2NotProfitableProfitableSite"
                            />

                            <InputField
                                v-model="
                                    form.variables.googleAds.adGroups
                                        .r3NotProfitableNotProfitableSite
                                "
                                label="Groupe d'annonce - R3-PasRentable-PasRentableSite"
                                field-name="variables.googleAds.adGroups.r3NotProfitableNotProfitableSite"
                                field-id="variables.googleAds.adGroups.r3NotProfitableNotProfitableSite"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter
            v-if="selectedTab !== 'products'"
            :is-submitting="isSubmitting"
            :errors="errors"
        >
            <template #actions>
                <DeleteAction
                    v-if="googleAdsProductManagement"
                    :url="
                        route(
                            'front.projectManagement.sea.deleteProductManagement',
                            {
                                tenant: tenant.id,
                                googleAdsProductManagement:
                                    googleAdsProductManagement.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.sea.productManagements',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
